import React, { Fragment, useEffect, useState } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { MatchPhase } from '../../../typings/match';
import { isEmptyArray, isNotEmptyArray } from '../../../utils/array';
import { makeClassNames } from '../../../utils/styles';
import ErrorComponent from '../../atoms/error/error';
import Hr from '../../atoms/hr/hr';
import NegativeMargin from '../../atoms/negative-margin/negative-margin';
import AdditiveFilterComponent, { IAdditiveFilterModel } from '../../organisms/additive-filter/additive-filter';
import ResultsTable, { IMatchDayList } from '../../organisms/results-table/results-table';
import ErrorBoundary from '../error-boundary/error-boundary';

export interface ITeamCompetition extends IMatchDayList {
  customCompetitionName: string;
  month: string;
}

export interface ITeamCalendarResults extends IComponent {
  competitionFilterModel?: IAdditiveFilterModel[];
  competitionList: ITeamCompetition[];
  teamId?: string | number; // FIXME: is it useful for this DS component? See livedata-football-widgets/src/widgets/team-calendar-results/transformer/query-transformer.ts
}

interface ISelectedCompetitionAndMonth {
  competitionName: string;
  monthName: string;
  competitionIndex: number;
  monthIndex: number;
}

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidCompetitionList = (list: ITeamCompetition[]): Boolean => Boolean(list && list.length);

const getUpdatedCompetitionList = (
  list: ITeamCompetition[],
  { competitionName, monthName, competitionIndex, monthIndex }: ISelectedCompetitionAndMonth
): ITeamCompetition[] =>
  list
    .filter((competition: ITeamCompetition) =>
      competitionIndex
        ? competition.customCompetitionName === competitionName || competition.month === competitionName
        : true
    )
    .filter((competition: ITeamCompetition) => (monthIndex ? competition.month === monthName : true));

const getRoundName = (competitionPhase?: MatchPhase, round?: number): string =>
  `${competitionPhase || (round ? `G${round}` : '')}${competitionPhase || round ? ' - ' : ''}`;

const classNames = () =>
  makeClassNames({
    teamCalendarResults: 'team-calendar-results',
  });

const TeamCalendarResults = ({
  /* istanbul ignore next: default value  */ competitionList = [],
  /* istanbul ignore next: default value  */ competitionFilterModel = [],
  teamId,
  trackingData,
}: ITeamCalendarResults) => {
  const cn = classNames();
  const [filteredCompetitionList, setFilteredCompetitionList] = useState(competitionList);

  const onChange = (competitionIndex: number, monthIndex: number) => {
    const competitionName = competitionFilterModel[competitionIndex]?.name;
    const monthName = competitionFilterModel[competitionIndex]?.secondaryDropdownModel?.[monthIndex]?.label || '';
    setFilteredCompetitionList(
      getUpdatedCompetitionList(competitionList, { competitionName, monthName, competitionIndex, monthIndex })
    );
  };

  useEffect(() => {
    // Due to useState() not been called correctly on 1st render, "filteredCompetitionList" is empty and "no data" error message is displayed
    // console.log('useEffect', { competitionFilterModel, competitionList, filteredCompetitionList });
    if (
      isNotEmptyArray(competitionFilterModel) &&
      isNotEmptyArray(competitionList) &&
      isEmptyArray(filteredCompetitionList)
    ) {
      setFilteredCompetitionList(competitionList); // or onChange(0, 0)?
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionList]); // Called on 1st render only

  return (
    <div className={cn.teamCalendarResults}>
      <ErrorBoundary hasError={!isValidCompetitionList(filteredCompetitionList)} errorComponent={getErrorComponent()}>
        {competitionFilterModel && competitionFilterModel.length ? (
          <Fragment>
            <AdditiveFilterComponent
              onChange={onChange}
              filterModel={competitionFilterModel}
              primaryDropdownTitle={t('team.results.filter.competitions')}
              secondaryDropdownTitle={t('team.results.filter.months')}
            />
            <Hr />
            <NegativeMargin />
          </Fragment>
        ) : null}
        {filteredCompetitionList &&
          filteredCompetitionList.map((competition: ITeamCompetition, i: number) => {
            // From ITeamCompetition to IMatchDayList
            const {
              competitionId,
              customCompetitionName: title = '',
              date,
              matchList,
              roundGroup,
              round,
              seasonId,
              competitionPhase,
              competitionPhaseRound,
            } = competition;
            const matchDayList: IMatchDayList[] = [
              {
                competitionId,
                competitionPhase,
                competitionPhaseRound,
                date,
                matchList,
                round,
                roundGroup,
                roundName: getRoundName(competitionPhase, round),
                seasonId,
                title,
              },
            ];

            return (
              <ResultsTable
                key={i}
                matchDayList={matchDayList}
                margin={false}
                showMatchRowLiveResult={false}
                trackingData={trackingData}
                type="results-thick"
              />
            );
          })}
      </ErrorBoundary>
    </div>
  );
};

export default TeamCalendarResults;
